import { Button } from '@components/ui/button.tsx'
import { Chip } from '@components/ui/chip.tsx'
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@components/ui/command.tsx'
import { Popover, PopoverAnchor, PopoverContent, PopoverTrigger } from '@components/ui/popover.tsx'
import { useTranslations } from '@utils/i18n-to-migrate/useTranslations'
import { cn } from '@utils/shad-utils.ts'
import { Check, ChevronsUpDown, X } from 'lucide-react'
import * as React from 'react'

export interface ComboboxItem<T> {
  value: T
  label: string
  description?: string
  displayLabel?: string
  group?: string
  groupId?: string
  itemClassName?: string
  disabled?: boolean
}

interface ComboboxBaseAsyncProps<T> {
  onSearchChange?: (search: string) => void
  onOpenChange?: (open: boolean) => void
  isLoading?: boolean
  isError?: boolean
  loadingText?: string
  errorText?: string
  selectedItem?: ComboboxItem<T>
  searchLabel?: string
}

interface ComboboxBasePropsSingle<T> extends ComboboxBaseAsyncProps<T> {
  items: ComboboxItem<T>[]
  value: T | null
  onSelect: (value: T | null) => void
  onBlur?: () => void
  icon?: React.ReactNode
  placeholder?: string
  searchPlaceholder?: string
  noResultsText?: string
  required?: boolean
  showSearch?: boolean
  sortSelected?: boolean
  multiple?: false
  hasError?: boolean
  twoLineItems?: boolean
}

interface ComboboxBasePropsMultiple<T> extends ComboboxBaseAsyncProps<T> {
  items: ComboboxItem<T>[]
  value: T[]
  onSelect: (value: T[]) => void
  onBlur?: () => void
  icon?: never
  placeholder?: string
  searchPlaceholder?: string
  noResultsText?: string
  required?: boolean
  showSearch?: boolean
  sortSelected?: boolean
  multiple: true
  hasError?: boolean
  twoLineItems?: boolean
}

type ComboboxBaseProps<T> = ComboboxBasePropsSingle<T> | ComboboxBasePropsMultiple<T>

type ComboboxBaseButtonProps = Omit<
  React.ComponentPropsWithoutRef<'button'>,
  'value' | 'onSelect' | 'onBlur'
>

type ComboboxBaseComponent = <T extends string | number>(
  props: ComboboxBaseProps<T> & ComboboxBaseButtonProps & React.RefAttributes<HTMLButtonElement>,
) => React.ReactElement | null

interface ComboboxItemGroup<T> {
  key: string
  heading: string
  items: ComboboxItem<T>[]
}

const groupItems = <T,>(items: ComboboxItem<T>[]): ComboboxItemGroup<T>[] => {
  const groups = new Map<string, ComboboxItemGroup<T>>()

  for (const item of items) {
    const key = item.groupId ?? item.group ?? ''
    const group = groups.get(key)

    if (group) {
      group.items.push(item)
    } else {
      groups.set(key, { key, heading: item.group ?? '', items: [item] })
    }
  }

  return Array.from(groups.values())
}

const isSameItem = <T,>(item: ComboboxItem<T> | undefined, other: ComboboxItem<T>): boolean =>
  item?.value === other.value &&
  item.label === other.label &&
  item.displayLabel === other.displayLabel

const ComboboxBaseInner = <T extends string | number>(
  {
    items,
    value,
    onSelect,
    onBlur,
    icon,
    placeholder,
    searchPlaceholder,
    required = false,
    noResultsText,
    showSearch = true,
    sortSelected = false,
    multiple = false,
    hasError = false,
    twoLineItems = false,
    onSearchChange,
    onOpenChange,
    isLoading = false,
    isError = false,
    loadingText,
    errorText,
    selectedItem: fallbackSelectedItem,
    searchLabel,
    ...props
  }: ComboboxBaseProps<T> & ComboboxBaseButtonProps,
  ref: React.ForwardedRef<HTMLButtonElement>,
) => {
  const { t } = useTranslations()
  const [open, setOpen] = React.useState(false)
  const [search, setSearch] = React.useState('')
  const [lastShownItem, setLastShownItem] = React.useState<ComboboxItem<T>>()
  const isServerSearch = onSearchChange !== undefined

  const handleSearchChange = (nextSearch: string) => {
    setSearch(nextSearch)
    onSearchChange?.(nextSearch)
  }

  const handleOpenChange = (nextOpen: boolean) => {
    setOpen(nextOpen)
    onOpenChange?.(nextOpen)
    if (!nextOpen) {
      onBlur?.()
      if (isServerSearch && search !== '') {
        handleSearchChange('')
      }
    }
  }

  const isMultiple = multiple

  const selectedValues = React.useMemo(() => {
    if (isMultiple) {
      return Array.isArray(value) ? value : []
    }
    return value ? [value] : []
  }, [value, isMultiple])

  const availableItems = React.useMemo(() => {
    if (!isMultiple) return items
    return items.filter((item) => !selectedValues.includes(item.value))
  }, [items, selectedValues, isMultiple])

  const sortedItems = React.useMemo(() => {
    if (!sortSelected || !isMultiple) {
      if (!sortSelected || !value) return availableItems

      return [...availableItems].sort((a, b) => {
        if (a.value === value) return -1
        if (b.value === value) return 1
        return 0
      })
    }
    return availableItems
  }, [availableItems, value, sortSelected, isMultiple])

  const itemGroups = React.useMemo(() => groupItems(sortedItems), [sortedItems])

  const selectedItems = React.useMemo(() => {
    return items.filter((item) => selectedValues.includes(item.value))
  }, [items, selectedValues])

  const handleSingleSelect = (itemValue: T) => {
    if (required && itemValue === value) {
      handleOpenChange(false)
      return
    }
    ;(onSelect as (value: T | null) => void)(itemValue === value ? null : itemValue)
    handleOpenChange(false)
  }

  const handleMultipleSelect = (itemValue: T) => {
    const currentValues = Array.isArray(value) ? value : []
    const newValues = currentValues.includes(itemValue)
      ? currentValues.filter((v) => v !== itemValue)
      : currentValues.concat(itemValue)
    ;(onSelect as (value: T[]) => void)(newValues)
  }

  const handleChipRemove = (itemValue: T) => {
    if (isMultiple) {
      const currentValues = Array.isArray(value) ? value : []
      const newValues = currentValues.filter((v) => v !== itemValue)
      ;(onSelect as (value: T[]) => void)(newValues)
    }
  }

  const handleClearAll = (e: React.MouseEvent) => {
    e.stopPropagation()
    if (isMultiple) {
      ;(onSelect as (value: T[]) => void)([])
    }
  }

  const getEmptyText = (): string => {
    if (isError && errorText) return errorText
    if (isLoading && loadingText) return loadingText
    return noResultsText ?? t('general.noResults')
  }

  const knownSelectedItem =
    !isMultiple && value
      ? (items.find((item) => item.value === value) ??
        (fallbackSelectedItem?.value === value ? fallbackSelectedItem : undefined))
      : undefined

  React.useEffect(() => {
    if (!isServerSearch || !knownSelectedItem) return
    setLastShownItem((previous) =>
      isSameItem(previous, knownSelectedItem) ? previous : knownSelectedItem,
    )
  }, [isServerSearch, knownSelectedItem])

  const selectedItem =
    knownSelectedItem ??
    (isServerSearch && value && lastShownItem?.value === value ? lastShownItem : undefined)
  const displayLabel = !isMultiple
    ? (selectedItem?.displayLabel ?? selectedItem?.label ?? placeholder)
    : placeholder

  return (
    <Popover open={open} onOpenChange={handleOpenChange}>
      {isMultiple ? (
        <PopoverAnchor asChild>
          <div
            className={cn(
              'flex min-h-9 w-full items-center gap-1.5 rounded-md border border-input bg-background px-3 text-sm shadow-xs',
              'has-[:focus-visible]:ring-4 has-[:focus-visible]:ring-ring/10 has-[:focus-visible]:ring-offset-0',
              'has-[:disabled]:pointer-events-none has-[:disabled]:cursor-not-allowed has-[:disabled]:opacity-50',
              hasError && 'border-destructive ring-destructive/20',
              props.className,
            )}
          >
            <div className="flex flex-1 flex-wrap items-center gap-1.5 min-w-0">
              {selectedItems.map((item) => (
                <Chip
                  key={item.value}
                  label={item.label}
                  onRemove={() => {
                    handleChipRemove(item.value)
                  }}
                  disabled={props.disabled}
                />
              ))}
              <PopoverTrigger asChild>
                <button
                  type="button"
                  {...props}
                  ref={ref}
                  role="combobox"
                  aria-expanded={open}
                  className={cn(
                    'bg-transparent border-none p-0 text-sm text-left focus:outline-none flex-1 min-h-6',
                    selectedItems.length === 0 ? 'min-w-[60px] py-0.5' : 'min-w-0 basis-0',
                  )}
                >
                  {selectedItems.length === 0 && (
                    <span className="text-muted-foreground">{placeholder}</span>
                  )}
                </button>
              </PopoverTrigger>
            </div>
            {selectedItems.length > 0 && !props.disabled && (
              <button
                type="button"
                onClick={handleClearAll}
                className="flex items-center text-muted-foreground hover:text-foreground focus:outline-none focus-visible:ring-2 focus-visible:ring-ring rounded-sm"
                aria-label={t('components.combobox.clearAll')}
              >
                <X className="h-4 w-4" />
              </button>
            )}
            <PopoverTrigger asChild>
              <button
                type="button"
                tabIndex={-1}
                aria-label={t('components.combobox.toggleDropdown')}
                className={cn(
                  'flex items-center self-stretch pl-2 focus:outline-none',
                  selectedItems.length > 0 && !props.disabled && 'border-l border-input',
                )}
              >
                <ChevronsUpDown className="h-4 w-4 shrink-0 opacity-50" />
              </button>
            </PopoverTrigger>
          </div>
        </PopoverAnchor>
      ) : (
        <PopoverTrigger asChild>
          <Button
            {...props}
            ref={ref}
            variant="outline"
            role="combobox"
            aria-expanded={open}
            className={cn('w-full', displayLabel || icon ? 'justify-between' : 'justify-end')}
          >
            <span className="flex items-center gap-2 min-w-0">
              {icon}
              <span className="truncate">{displayLabel}</span>
            </span>
            <ChevronsUpDown
              className={cn('h-4 w-4 shrink-0 opacity-50', value || displayLabel ? 'ml-2' : '')}
            />
          </Button>
        </PopoverTrigger>
      )}
      <PopoverContent align="start" className="p-0 w-(--radix-popper-anchor-width) cursor-pointer">
        <Command
          className="w-full"
          label={searchLabel}
          shouldFilter={!isServerSearch}
          filter={
            showSearch && !isServerSearch
              ? (value, search, keywords = []) => {
                  const extendValue = `${value} ${keywords.join(' ')}`
                  if (extendValue.toLowerCase().includes(search.toLowerCase())) {
                    return 1
                  }
                  return 0
                }
              : undefined
          }
        >
          {showSearch && (
            <CommandInput
              placeholder={searchPlaceholder}
              isLoading={isLoading}
              value={isServerSearch ? search : undefined}
              onValueChange={isServerSearch ? handleSearchChange : undefined}
            />
          )}
          <CommandList className="w-full">
            <CommandEmpty>{getEmptyText()}</CommandEmpty>
            {itemGroups.map((group) => (
              <CommandGroup key={group.key} heading={group.heading || undefined}>
                {group.items.map((item) => (
                  <CommandItem
                    className={cn('cursor-pointer', twoLineItems && 'py-2', item.itemClassName)}
                    disabled={item.disabled}
                    key={item.value}
                    value={String(item.value)}
                    keywords={[item.label, item.description ?? '']}
                    onSelect={() => {
                      if (isMultiple) {
                        handleMultipleSelect(item.value)
                      } else {
                        handleSingleSelect(item.value)
                      }
                    }}
                  >
                    {twoLineItems ? (
                      <div className="flex flex-col gap-0.5">
                        <span>{item.label}</span>
                        {item.description && (
                          <span className="text-xs text-muted-foreground">{item.description}</span>
                        )}
                      </div>
                    ) : (
                      item.label
                    )}
                    {!isMultiple && (
                      <Check
                        className={cn(
                          'mr-2 h-4 w-4 ml-auto',
                          value === item.value ? 'opacity-100' : 'opacity-0',
                        )}
                      />
                    )}
                  </CommandItem>
                ))}
              </CommandGroup>
            ))}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  )
}

const ComboboxBaseForwardRef = React.forwardRef(ComboboxBaseInner)
ComboboxBaseForwardRef.displayName = 'ComboboxBase'

export const ComboboxBase = ComboboxBaseForwardRef as unknown as ComboboxBaseComponent
